import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ALERT_FLOW_COLUMNS,
  ALERT_FLOW_ORDER,
  alertFlowKind,
  buildAlertFlowFlows,
  buildAlertFlowNodes,
  type AlertFlowCounts,
  type AlertFlowNode,
} from '../../lib/alertFlow';

interface AlertFlowPathProps {
  counts: AlertFlowCounts;
  /** "In campaigns" opens the campaigns. */
  onOpenCampaigns?: () => void;
}

interface Box {
  key: string;
  node: AlertFlowNode;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Ribbon {
  s: string;
  t: string;
  h: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

const fmt = (n: number) => n.toLocaleString('en-US');
const shortLabel = (label: string) => label.split(' — ')[0];

/** The narrowest the drawing is made; a narrower page scrolls it sideways. */
const MIN_W = 1100;
const PAD_L = 24;
/** Room on the right for the labels of the last column ("Awaiting decision"). */
const PAD_R = 140;
const TOP = 92;
/** The main path's lane, and the lane under it where what each stage held back lands. */
const LANE_H = 220;
const LEAK_Y = TOP + LANE_H + 56;
const H = LEAK_Y + 110;
const NODE_W = 12;
const GAP = 10;
/** A node shorter than this still takes this much room, so its figure and label never meet the next one's. */
const LABEL_ROOM = 34;

function useWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(1200);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setW(Math.max(MIN_W, el.clientWidth));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

/** Ribbons between boxes, stacked inside each box top to bottom by the other end's place, so they do not cross. */
function buildRibbons(flows: [string, string, number][], boxes: Record<string, Box>, k: number): Ribbon[] {
  const list = flows.filter(([s, t]) => boxes[s] && boxes[t]);
  const height = (v: number) => Math.max(1.5, v * k);
  const outY = new Map<(typeof list)[number], number>();
  const inY = new Map<(typeof list)[number], number>();
  const used: Record<string, number> = {};
  [...list]
    .sort((a, b) => boxes[a[0]].y - boxes[b[0]].y || boxes[a[1]].y - boxes[b[1]].y)
    .forEach((f) => {
      const h = height(f[2]);
      const key = `o:${f[0]}`;
      used[key] = used[key] ?? 0;
      outY.set(f, boxes[f[0]].y + used[key] + h / 2);
      used[key] += h;
    });
  [...list]
    .sort((a, b) => boxes[a[1]].y - boxes[b[1]].y || boxes[a[0]].y - boxes[b[0]].y)
    .forEach((f) => {
      const h = height(f[2]);
      const key = `i:${f[1]}`;
      used[key] = used[key] ?? 0;
      inY.set(f, boxes[f[1]].y + used[key] + h / 2);
      used[key] += h;
    });
  return list.map((f) => ({
    s: f[0],
    t: f[1],
    h: height(f[2]),
    x1: boxes[f[0]].x + boxes[f[0]].w,
    y1: outY.get(f)!,
    x2: boxes[f[1]].x,
    y2: inY.get(f)!,
  }));
}

const ribbonPath = (r: Ribbon) => {
  const mx = (r.x1 + r.x2) / 2;
  const a = r.h / 2;
  return `M${r.x1},${r.y1 - a} C${mx},${r.y1 - a} ${mx},${r.y2 - a} ${r.x2},${r.y2 - a} L${r.x2},${r.y2 + a} C${mx},${r.y2 + a} ${mx},${r.y1 + a} ${r.x1},${r.y1 + a} Z`;
};

/**
 * The Alert Flow drawn as its main path (docs/c3/DESIGN.md, Alert Flow): one indigo lane runs through the seven
 * pipeline stages with each stage's figure above it, what a stage holds back drops to a lane below (duplicates,
 * baseline normal, false positives), and the last stage opens into where each remediation stands. The nodes and the
 * flows are the ones `src/lib/alertFlow.ts` builds from the backend's counters; nothing is counted here.
 */
const AlertFlowPath: React.FC<AlertFlowPathProps> = ({ counts, onOpenCampaigns }) => {
  const nodes = useMemo(() => buildAlertFlowNodes(counts), [counts]);
  const flows = useMemo(() => buildAlertFlowFlows(counts), [counts]);
  const [ref, W] = useWidth();
  const [active, setActive] = useState<string | null>(null);

  const cols: string[][] = [[], [], [], [], [], [], []];
  Object.entries(nodes).forEach(([key, node]) => cols[node.col].push(key));
  cols.forEach((keys, i) => {
    const order = ALERT_FLOW_ORDER[i];
    if (order) keys.sort((a, b) => order.indexOf(a) - order.indexOf(b));
  });
  const colX = ALERT_FLOW_COLUMNS.map((_, i) => PAD_L + (i * (W - PAD_L - PAD_R)) / 6);
  const colW = (W - PAD_L - PAD_R) / 6;

  // One scale for the whole drawing: the tallest lane (the main path of a stage, or the whole last column) fills it.
  const maxTotal = Math.max(
    1,
    ...cols.map((keys, i) =>
      keys
        .filter((key) => i === 6 || alertFlowKind(key) === 'main')
        .reduce((sum, key) => sum + nodes[key].v, 0)
    )
  );
  const k = (LANE_H - GAP * 4) / maxTotal;
  const boxes: Record<string, Box> = {};
  cols.forEach((keys, i) => {
    const x = colX[i] - NODE_W / 2;
    let y = TOP;
    let leakY = LEAK_Y;
    keys.forEach((key) => {
      const h = Math.max(3, nodes[key].v * k);
      if (i === 6) {
        boxes[key] = { key, node: nodes[key], x, y, w: NODE_W, h };
        y += Math.max(h, LABEL_ROOM) + 12;
      } else if (alertFlowKind(key) === 'main') {
        boxes[key] = { key, node: nodes[key], x, y, w: NODE_W, h };
        y += h + GAP;
      } else {
        boxes[key] = { key, node: nodes[key], x: x + colW * 0.42, y: leakY, w: NODE_W, h };
        leakY += Math.max(h, 18) + 26;
      }
    });
  });
  const ribbons = buildRibbons(flows, boxes, k);

  const labelOf = (key: string) =>
    key === 'camp' ? `In ${fmt(counts.campaigns)} campaign${counts.campaigns === 1 ? '' : 's'}` : shortLabel(nodes[key].label);
  const share = (key: string) => {
    const remediation = nodes[key].col >= 5;
    const whole = remediation ? counts.remediations : counts.raw;
    return `${Math.round((nodes[key].v / Math.max(1, whole)) * 100)}% of ${remediation ? 'remediations' : 'raw alerts'}`;
  };
  const describe = (key: string) => `${fmt(nodes[key].v)} ${labelOf(key)} · ${share(key)} · ${nodes[key].d}`;
  const info = active && nodes[active] ? describe(active) : 'Point at a figure, or move to it with Tab, to see what it counts.';
  const summary = `Alert Flow: ${fmt(counts.raw)} raw alerts, ${fmt(counts.unique)} unique, ${fmt(counts.anomalies)} anomalies, ${fmt(
    counts.threats
  )} threats, ${fmt(counts.remediations)} remediations, ${fmt(counts.remCompleted)} completed.`;

  return (
    <>
      <div className="af-panel" ref={ref}>
        <div className="af-cols" style={{ width: W }} aria-hidden>
          {ALERT_FLOW_COLUMNS.map((column, i) => (
            <div key={column.id} className="af-col-head" style={{ left: colX[i] - 6 }}>
              <span className="af-idx">{String(i + 1).padStart(2, '0')}</span>
              <span className="af-name">{column.title}</span>
            </div>
          ))}
        </div>
        <svg width={W} height={H} className="af-svg" role="group" aria-label={summary}>
          {ribbons.map((r, i) => (
            <path
              key={i}
              d={ribbonPath(r)}
              className={`af-rib k-${alertFlowKind(r.t)}${active && (r.s === active || r.t === active) ? ' is-hot' : ''}`}
            />
          ))}
          {Object.values(boxes).map((b) => {
            const kind = alertFlowKind(b.key);
            // The first node of each stage carries the stage's figure, large, above the lane: the main path's, and in the
            // last stage the first state (completed), so the seventh column reads like the other six.
            const lead =
              b.node.col === 6
                ? cols[6][0] === b.key
                : kind === 'main' && cols[b.node.col].find((key) => alertFlowKind(key) === 'main') === b.key;
            const opens = b.key === 'camp' && Boolean(onOpenCampaigns);
            const lx = b.x + b.w + 10;
            const cy = b.y + b.h / 2;
            return (
              <g
                key={b.key}
                className={`af-node k-${kind}${active === b.key ? ' is-hot' : ''}${opens ? ' is-link' : ''}`}
                tabIndex={0}
                role={opens ? 'button' : 'img'}
                aria-label={opens ? `${describe(b.key)}. Opens the campaigns.` : describe(b.key)}
                onMouseEnter={() => setActive(b.key)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(b.key)}
                onBlur={() => setActive(null)}
                onClick={opens ? () => onOpenCampaigns?.() : undefined}
                onKeyDown={
                  opens
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onOpenCampaigns?.();
                        }
                      }
                    : undefined
                }
              >
                <rect className="af-bar" x={b.x} y={b.y} width={b.w} height={b.h} rx={3} />
                <rect className="af-hit" x={b.x - 8} y={b.y - 6} width={b.w + 16} height={b.h + 12} />
                {lead ? (
                  <>
                    <text x={b.x} y={b.y - 32} className="af-v af-v-big">
                      {fmt(b.node.v)}
                    </text>
                    <text x={b.x} y={b.y - 10} className="af-l">
                      {labelOf(b.key)}
                    </text>
                  </>
                ) : (
                  <>
                    <text x={lx} y={cy - 2} className="af-v">
                      {fmt(b.node.v)}
                    </text>
                    <text x={lx} y={cy + 14} className="af-l">
                      {labelOf(b.key)}
                    </text>
                  </>
                )}
              </g>
            );
          })}
          <text x={PAD_L} y={H - 14} className="af-lane-label">
            Below the line: what each stage held back
          </text>
        </svg>
      </div>
      <p className="af-info" aria-live="polite">
        {info}
      </p>
    </>
  );
};

export default AlertFlowPath;
